import api from './client';

export const BlishAPI = {
    auth: {
        checkEmail: (email) => api.post('/auth/check-email', { email }, { skipAuth: true }),
        login: (credentials) => api.post('/auth/login', credentials, { skipAuth: true }),
        register: (data) => api.post('/auth/register', data, { skipAuth: true }),
        verifyEmail: (data) => api.post('/auth/verify-email', data, { skipAuth: true }),
        getProfile: () => api.get('/auth/me'),
        updateProfile: (data) => api.put('/auth/me', data),
        logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
        forgotPassword: (email) => api.post('/auth/forgot-password', { email }, { skipAuth: true }),
        resetPassword: (data) => api.post('/auth/reset-password', data, { skipAuth: true }),
        resendVerification: (email) => api.post('/auth/resend-verification', { email }, { skipAuth: true }),
        changePassword: (data) => api.post('/auth/change-password', data),
        getReferral: () => api.get('/auth/me/referral'),
        createReferral: () => api.post('/auth/me/referral'),

        // Gaming platform auth
        gamingPlatformStatus: () => api.get('/auth/gaming-platform/status', { skipAuth: true }),
        gamingPlatformInit: (data) => api.post('/auth/gaming-platform/init', data, { skipAuth: true }),
        gamingPlatformCallback: (data) => api.post('/auth/gaming-platform/callback', data, { skipAuth: true }),
        gamingPlatformLinks: () => api.get('/auth/gaming-platform/links'),
        gamingPlatformUnlink: (platform) => api.delete(`/auth/gaming-platform/unlink/${platform}`),

        // Google OAuth
        googleSession: (data) => api.post('/auth/google/session', data, { skipAuth: true }),

        // Phone verification
        phoneSendOtp: (data) => api.post('/auth/phone/send-otp', data),
        phoneVerifyOtp: (data) => api.post('/auth/phone/verify-otp', data),
    },

    wallet: {
        getWallet: () => api.get('/wallet'),
        getTransactions: (limit = 50, offset = 0) => api.get('/wallet/transactions', { params: { limit, offset } }),
        getPinStatus: () => api.get('/wallet/pin-status'),
        setPin: (pin) => api.post('/wallet/set-pin', { pin }),
        requestPinReset: () => api.post('/wallet/forgot-pin'),
        resetPin: (data) => api.post('/wallet/reset-pin', data),
        withdraw: (data, idempotencyKey) =>
            api.post('/wallet/withdraw', data, { headers: { 'X-Idempotency-Key': idempotencyKey } }),
        withdrawPaystack: (data) => api.post('/wallet/paystack/withdraw', data),
    },

    challenges: {
        submitReview: (id, data) => api.post(`/reviews/${id}`, data),
        rematch: (id) => api.post(`/challenges/${id}/rematch`, {}),
        myHistory: () => api.get('/challenges/my-history'),
        create: (data) => api.post('/challenges', data),
        list: (status) => api.get('/challenges', { params: { status } }),
        getDetails: (id) => api.get(`/challenges/${id}`),
        join: (id) => api.post(`/challenges/${id}/join`),
        reject: (id) => api.post(`/challenges/${id}/reject`),
        declareResult: (id, winnerId) => api.post(`/challenges/${id}/declare-result`, { declared_winner_id: winnerId }),
        confirmResult: (id, accepted) => api.post(`/challenges/${id}/confirm-result`, { accepted }),
        submitDispute: (id, formData) => api.post(`/challenges/${id}/dispute-with-proof`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        submitLivestreamDispute: (id, data) => api.post(`/challenges/${id}/dispute-with-proof`, data),
        respondToAi: (id, accepted) => api.post(`/challenges/${id}/respond-ai-decision`, { accepted }),
        getFeedbackStatus: (id) => api.get(`/challenges/${id}/feedback-status`),
        sendFeedback: (id, data) => api.post(`/challenges/${id}/feedback`, data),
        canReview: (id) => api.get(`/reviews/can-review/${id}`),
        shareVictory: (id) => api.post(`/challenges/${id}/victory-share`),
        getShareStatus: (id) => api.get(`/challenges/${id}/share-status`),
    },

    dashboard: {
        getStats: () => api.get('/dashboard/stats'),
    },

    players: {
        getPlayer: (id) => api.get(`/auth/${id}/stats`),
        list: (params) => api.get('/players', { params }),
        backPlayer: (id, amount) => api.post(`/players/${id}/back`, { amount }),
        optOut: (backingId) => api.delete(`/backings/${backingId}`),
        getMyBackings: () => api.get('/me/backings'),
        getMyBackers: () => api.get('/me/backers'),
        startChat: (playerId) => api.post(`/users/${playerId}/start-chat`),
        getAvailablePlayers: (selectedRank) => api.get('/players/available', {
            params: selectedRank ? { rank: selectedRank } : {}
        }),
        searchPlayer: (searchQuery) => api.get(`/users/search?q=${encodeURIComponent(searchQuery)}`),
    },

    tournaments: {
        list: (status = 'all', game) => api.get('/tournaments', { params: { status, ...(game ? { game } : {}) } }),
        getDetails: (id) => api.get(`/tournaments/${id}`),
        join: (id) => api.post(`/tournaments/${id}/join`),
        create: (data) => api.post('/tournaments', data),
        reportMatchResult: (tourneyId, matchId, winnerId) =>
            api.post(`/tournaments/${tourneyId}/matches/${matchId}/result`, { winner_id: winnerId }),
        confirmMatchResult: (tourneyId, matchId, accepted) =>
            api.post(`/tournaments/${tourneyId}/matches/${matchId}/confirm`, { accepted }),
        retryGameMatch: (tourneyId, matchId) =>
            api.post(`/tournaments/${tourneyId}/matches/${matchId}/retry-game`),
    },

    kyc: {
        getStatus: () => api.get('/kyc/status'),
        submitBasic: (formData) => api.post('/kyc/submit-basic', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        submitWithdrawal: (formData) => api.post('/kyc/submit-withdrawal', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
    },

    payments: {
        getRegion: () => api.get('/payment/detect-region'),
        getPackagesUSD: () => api.get('/payment/packages'),
        getPackagesNGN: () => api.get('/payment/packages-ngn'),
        initializePaystack: (data) => api.post('/payment/paystack/initialize', data),
        verifyPaystack: (reference) => api.get(`/payment/paystack/verify/${reference}`),
        getBanks: () => api.get('/payment/banks'),
        verifyBankAccount: (code, num) => api.post(`/payment/paystack/verify-account?bank_code=${code}&account_number=${num}`),
        getPricingConfig: () => api.get('/pricing/config'),
    },

    nowpayments: {
        createPayment: (data) => api.post('/payment/nowpayments/create-payment', data),
        getStatus: (orderId) => api.get(`/payment/nowpayments/status/${orderId}`),
        withdraw: (data, idempotencyKey) =>
            api.post('/payment/nowpayments/withdraw', data, { headers: { 'X-Idempotency-Key': idempotencyKey } }),
        getCurrencies: () => api.get('/payment/nowpayments/currencies'),
        getWithdrawCurrencies: () => api.get('/payment/nowpayments/withdraw-currencies'),
    },

    messaging: {
        getConversations: () => api.get('/messages/conversations'),
        getMessages: (convId) => api.get(`/messages/conversation/${convId}`),
        sendMessage: (convId, content) => api.post('/messages/send', { conversation_id: convId, content }),
        startConversation: (data) => api.post('/messages/start-conversation', data),
    },

    match: {
        findMatch: (data) => api.post('/matchmaking/find', data),
        queue: (formData) => api.get(`/matchmaking/queue?game=${formData.game}&entry_fee=${formData.entry_fee}`),
        cancel: (formData) => api.delete(`/matchmaking/cancel?game=${formData.game}&entry_fee=${formData.entry_fee}`),
    },

    social: {
        getLeaderboard: (limit = 100) => api.get('/seasons/leaderboard', { params: { limit } }),
        getCurrentSeason: () => api.get('/seasons/current'),
        getNotifications: () => api.get('/notifications'),
        markNotificationRead: (id) => api.patch(`/notifications/${id}/read`),
    },

    public: {
        getLandingChallenges: () => api.get('/public/challenges', { skipAuth: true }),
        getLandingStats: () => api.get('/public/stats', { skipAuth: true }),
        getMatchups: () => api.get('/public/matchups', { skipAuth: true }),
        searchPlayer: (query) => api.get(`/public/player-search?q=${encodeURIComponent(query)}`, { skipAuth: true }),
    },

    notifications: {
        getNotifications: (limit = 20, offset = 0) => api.get('/notifications', { params: { limit, offset } }),
        markAsRead: (id) => api.patch(`/notifications/${id}/read`),
        markAllRead: () => api.patch('/notifications/read-all'),
    },

    support: {
        getMyChat: () => api.get('/support/my-chat'),
        startChat: (data) => api.post('/support/start-chat', data),
        getMessages: (chatId) => api.get(`/support/chat/${chatId}/messages`),
        sendMessage: (chatId, data) => api.post(`/support/chat/${chatId}/send`, data),
        closeChat: (chatId) => api.post(`/support/chat/${chatId}/close`),
    },

    streaming: {
        getFeatured: () => api.get('/streaming/featured'),
        getGames: () => api.get('/streaming/games'),
    },

    streams: {
        getLive: () => api.get('/streams/live'),
        getMySubscriptions: () => api.get('/streams/my-subscriptions'),
        getChat: (streamId) => api.get(`/streams/${streamId}/chat`),
        create: (data) => api.post('/streams', data),
        updateSettings: (streamId, data) => api.patch(`/streams/${streamId}/settings`, data),
        end: (streamId) => api.post(`/streams/${streamId}/end`),
        inviteGuest: (streamId, data) => api.post(`/streams/${streamId}/invite-guest`, data),
        acceptGuest: (streamId, data) => api.post(`/streams/${streamId}/accept-guest`, data),
        removeGuest: (streamId, data) => api.post(`/streams/${streamId}/remove-guest`, data),
        subscribe: (streamerId) => api.post(`/streams/${streamerId}/subscribe`),
        sendChat: (streamId, data) => api.post(`/streams/${streamId}/chat`, data),
        gift: (streamId, data) => api.post(`/streams/${streamId}/gift`, data),
    },

    reviews: {
        getPublic: (limit = 10) => api.get('/reviews/public', { params: { limit } }),
    },

    contact: {
        send: (formData) => api.post('/contact', formData, { skipAuth: true }),
    },

    complaints: {
        submit: (data) => api.post('/complaints', data, { skipAuth: true }),
        submitAuth: (data) => api.post('/complaints/auth', data),
    },

    tribunal: {
        getStatus: () => api.get('/tribunal/status'),
        apply: (data) => api.post('/tribunal/apply', data),
    },

};
